import * as csc from 'cornerstone-core';
// @ts-ignore
import * as csm from 'cornerstone-math';
import * as cst from 'cornerstone-tools';
import * as _ from 'lodash';
import {
  getCanvasDistance,
  offsetPixelPointInCanvas,
  projectAlongAxis,
  projectPerpendicularToAxis,
} from 'app/CornerstoneTools/pixelToCanvasUtils';
import {
  getLineIntersection,
  getLinesAngle,
  rotateVector,
} from 'app/CornerstoneTools/geometryHelper';
import toStringDegree from 'app/CornerstoneTools/geometry/toStringDegree';
import { drawToolStepExplanations } from 'app/CornerstoneTools/utils/toolStepsExplanations';
import {
  BaseAnnotationTool,
  getNewContext,
  draw,
  drawHandles,
  drawLine,
  drawTextBox,
  path,
  toolStyle,
  getToolForElement,
} from 'app/CornerstoneTools';
import checkPointNearHandles from 'app/CornerstoneTools/checkPointNearHandles';
import ChainingToolBuilder from 'app/CornerstoneTools/ChainingToolBuilder';
import { EventWithCornerstoneDetail, Point, TPLOAnnotation } from 'app/CornerstoneTools/types';
import { IntlShape } from 'react-intl';

const CORNERSTONE_TEXT_PADDING = 5;

export type TPLOToolConfiguration = cst.BaseToolConfiguration & {
  dicomDataPrinterToolName: string;
  drawHandles: boolean;
  drawHandlesOnHover: boolean;
  hideHandlesIfMoving: boolean;
  renderDashed: boolean;
  getIntl: () => IntlShape;
};

type Line = {
  start: Point;
  end: Point;
};

export const getTAProjection = (
  element: HTMLElement,
  { TA1, TA2, MTP1, MTP2 }: TPLOAnnotation['handles']
): Line | undefined => {
  if (!TA1 || !TA2 || !MTP1 || !MTP2) return undefined;

  const MTPLength = getCanvasDistance(element, MTP1, MTP2);
  const intersectionPoint = getLineIntersection(
    { start: TA1, end: TA2 },
    { start: MTP1, end: MTP2 }
  );
  if (!intersectionPoint) return undefined;

  return {
    start: projectPerpendicularToAxis(element, intersectionPoint, TA1, TA2, -MTPLength / 2),
    end: projectPerpendicularToAxis(element, intersectionPoint, TA1, TA2, MTPLength / 2),
  };
};

const getAcuteAngle = (angle: number): number => {
  const isWideAngle = Math.abs(angle) >= Math.PI / 2;
  return isWideAngle ? angle - csm.sign(angle) * Math.PI : angle;
};

const computeTPLOAngleWithTAProjection = (
  element: HTMLElement,
  { MTP1, MTP2 }: Pick<TPLOAnnotation['handles'], 'MTP1' | 'MTP2'>,
  TAProjection: Line
): number | undefined => {
  if (!TAProjection) return undefined;
  const TPLOAngle = getLinesAngle(
    [
      csc.pixelToCanvas(element, MTP1 as csc.PixelCoordinate),
      csc.pixelToCanvas(element, MTP2 as csc.PixelCoordinate),
    ],
    [
      csc.pixelToCanvas(element, TAProjection.start as csc.PixelCoordinate),
      csc.pixelToCanvas(element, TAProjection.end as csc.PixelCoordinate),
    ]
  );
  return getAcuteAngle(TPLOAngle);
};

export const computeTPLOAngle = (
  element: HTMLElement,
  handles: TPLOAnnotation['handles']
): number | undefined =>
  computeTPLOAngleWithTAProjection(element, handles, getTAProjection(element, handles));

export default class TPLOTool
  extends BaseAnnotationTool
  implements cst.IBaseAnnotationTool<TPLOAnnotation>
{
  public toolBuilder: ChainingToolBuilder<TPLOAnnotation>;
  public addNewMeasurement: ChainingToolBuilder<TPLOAnnotation>['addNewMeasurement'];
  public pointNearTool: typeof checkPointNearHandles;

  constructor(props: Partial<{ configuration: Partial<TPLOToolConfiguration> }> = {}) {
    const defaultProps = {
      name: 'TPLOTool',
      supportedInteractionTypes: ['Mouse', 'Touch'] as const,
      configuration: {
        dicomDataPrinterToolName: 'DicomDataPrinter',
        drawHandles: true,
        drawHandlesOnHover: false,
        hideHandlesIfMoving: false,
        renderDashed: false,
        getIntl: () => ({} as IntlShape),
      } as TPLOToolConfiguration,
    };

    super(props, defaultProps);
    this.toolBuilder = new ChainingToolBuilder({
      name: this.name,
      createNewMeasurement: this.createNewMeasurement,
      handlesKeys: ['TA1', 'TA2', 'MTP1', 'MTP2'],
      maximumToolData: 1,
    });
    this.addNewMeasurement = this.toolBuilder.addNewMeasurement;

    this.pointNearTool = checkPointNearHandles;
  }

  get configuration(): TPLOToolConfiguration {
    return super.configuration as TPLOToolConfiguration;
  }

  createNewMeasurement = (eventData: any): TPLOAnnotation =>
    ({
      visible: true,
      active: true,
      color: undefined,
      invalidated: true,
      handles: {
        TA1: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true,
        },
      },
    } as TPLOAnnotation);

  // TODO(REFACTO): All angle and projection could be cached to reduce computation on rerender
  // where tool is not modified.
  updateCachedStats = (_image: csc.Image, element: HTMLElement, data: TPLOAnnotation): void => {};

  drawTool = (element: HTMLElement, ctx: CanvasRenderingContext2D, data: TPLOAnnotation): void => {
    // Draw the handles
    const color = cst.toolColors.getColorIfActive(data);
    const { handleRadius, drawHandlesOnHover, hideHandlesIfMoving } = this.configuration;

    const handleOptions = {
      color,
      handleRadius,
      drawHandlesIfActive: drawHandlesOnHover,
      hideHandlesIfMoving,
    };

    const lineWidth = toolStyle.getToolWidth();

    drawHandles(ctx, { element }, data.handles, handleOptions);

    let isLeftSideView = true;
    const TAProjection = getTAProjection(element, data.handles);

    if (TAProjection) {
      drawLine(ctx, element, TAProjection.start, TAProjection.end, { color: 'red' });

      const TPLOCircleCenter = csc.pixelToCanvas(element, {
        x: (TAProjection.end.x + TAProjection.start.x) / 2,
        y: (TAProjection.end.y + TAProjection.start.y) / 2,
      } as csc.PixelCoordinate);

      const TPLOAngle = computeTPLOAngleWithTAProjection(element, data.handles, TAProjection);

      if (TPLOAngle !== undefined) {
        isLeftSideView = TPLOAngle < 0;

        // Using acute angle avoid error due to MTP1 and MTP2 being reversed.
        this.drawTPLOArc(
          element,
          ctx,
          data.handles,
          isLeftSideView,
          TAProjection,
          TPLOCircleCenter,
          TPLOAngle,
          { color, lineWidth }
        );
      }
    }

    this.drawTALine(element, ctx, data.handles, isLeftSideView, { color, lineWidth });
    this.drawMTPLine(element, ctx, data.handles, isLeftSideView, { color, lineWidth });
  };

  renderToolData = (evt: EventWithCornerstoneDetail): void => {
    const eventData = evt.detail;
    const { element } = eventData;
    // If we have no toolData for this element, return immediately as there is nothing to do
    const toolData = cst.getToolState(evt.currentTarget, this.name);
    const { getIntl, dicomDataPrinterToolName } = this.configuration;

    // We have tool data for this element - iterate over each one and draw it
    const context = getNewContext(eventData.canvasContext.canvas);
    draw(
      context,
      /** @param {CanvasRenderingContext2D} ctx */
      (ctx: CanvasRenderingContext2D) => {
        const dicomDataPrinterTextOffset =
          getToolForElement(element, dicomDataPrinterToolName)?.dicomTextArea?.topLeft?.y ?? 0;

        drawToolStepExplanations(
          ctx,
          toolData?.data,
          cst.getToolForElement(element, this.name).mode === 'active',
          ['tools.TPLO.steps.1', 'tools.TPLO.steps.2'].map(
            (id) => () => getIntl()?.formatMessage({ id })
          ),
          [
            ['TA1', 'TA2'],
            ['MTP1', 'MTP2'],
          ],
          {
            x: 5,
            y: dicomDataPrinterTextOffset + 20,
          },
          { maxWidth: (2 * eventData.canvasContext.canvas.width) / 5 }
        );

        toolData?.data.forEach((data: TPLOAnnotation) => {
          if (this.toolBuilder.checkToolCreationCancelled(element, data)) return;
          this.drawTool(element, ctx, data);
        });
      }
    );
  };

  drawTPLOArc = (
    element: HTMLElement,
    ctx: CanvasRenderingContext2D,
    handles: TPLOAnnotation['handles'],
    isLeftSideView: boolean,
    TAProjection: Line,
    TPLOCircleCenter: Point,
    TPLOAngle: number,
    { color, lineWidth }: { color: string; lineWidth: number }
  ): void => {
    const { MTP1, MTP2 } = handles;
    const XAxisToMTPAxisAcuteAngle = getAcuteAngle(
      getLinesAngle(
        [
          { x: 0, y: 0 },
          { x: 1, y: 0 },
        ],
        [
          csc.pixelToCanvas(element, MTP1 as csc.PixelCoordinate),
          csc.pixelToCanvas(element, MTP2 as csc.PixelCoordinate),
        ]
      )
    );

    let TPLOArcStart: number;
    let TPLOArcEnd: number;
    if (isLeftSideView) {
      TPLOArcStart = XAxisToMTPAxisAcuteAngle;
      TPLOArcEnd = TPLOArcStart + TPLOAngle;
    } else {
      TPLOArcStart = XAxisToMTPAxisAcuteAngle + Math.PI;
      TPLOArcEnd = TPLOArcStart + TPLOAngle;
    }

    const arcRadius =
      csm.point.distance(
        csc.pixelToCanvas(element, TAProjection.start as csc.PixelCoordinate),
        csc.pixelToCanvas(element, TAProjection.end as csc.PixelCoordinate)
      ) / 4;

    path(ctx, { color: 'red' }, (drawContext: CanvasRenderingContext2D) => {
      drawContext.arc(
        TPLOCircleCenter.x,
        TPLOCircleCenter.y,
        arcRadius,
        TPLOArcStart,
        TPLOArcEnd,
        isLeftSideView
      );
    });

    const projectionAxis = rotateVector({ x: 1, y: 0 }, (TPLOArcStart + TPLOArcEnd) / 2);

    const textBoxCoordinates = csc.pixelToCanvas(
      element,
      projectAlongAxis(
        element,
        csc.canvasToPixel(element, TPLOCircleCenter as csc.CanvasCoordinate),
        csc.canvasToPixel(element, { x: 0, y: 0 } as csc.CanvasCoordinate),
        csc.canvasToPixel(element, projectionAxis as csc.CanvasCoordinate),
        arcRadius + 20
      ) as csc.PixelCoordinate
    );

    ctx.save();
    if (!isLeftSideView) {
      ctx.textAlign = 'right';
    }
    drawTextBox(
      ctx,
      toStringDegree(Math.abs(TPLOAngle)),
      textBoxCoordinates.x - CORNERSTONE_TEXT_PADDING,
      textBoxCoordinates.y,
      color,
      { centering: { y: true } }
    );
    ctx.restore();
  };

  drawTALine = (
    element: HTMLElement,
    ctx: CanvasRenderingContext2D,
    { TA1, TA2 }: Pick<TPLOAnnotation['handles'], 'TA1' | 'TA2'>,
    isLeftSideView: boolean,
    { color, lineWidth }: { color: string; lineWidth: number }
  ): void => {
    if (!TA1 || !TA2) return;

    drawLine(ctx, element, TA1, TA2, { color, lineWidth });
    const TA1Canvas = csc.pixelToCanvas(element, TA1 as csc.PixelCoordinate);
    const TA2Canvas = csc.pixelToCanvas(element, TA2 as csc.PixelCoordinate);
    const bottomToTopTAAxis: [Point, Point] = TA2Canvas.y >= TA1Canvas.y ? [TA2, TA1] : [TA1, TA2];

    const offsetToTA = isLeftSideView ? -10 : 10;

    const TAAnnotationCoords = csc.pixelToCanvas(
      element,
      offsetPixelPointInCanvas(
        element,
        projectPerpendicularToAxis(element, bottomToTopTAAxis[0], ...bottomToTopTAAxis, offsetToTA),
        { x: -CORNERSTONE_TEXT_PADDING, y: -CORNERSTONE_TEXT_PADDING }
      ) as csc.PixelCoordinate
    );

    ctx.save();
    if (isLeftSideView) {
      ctx.textAlign = 'right';
    }
    drawTextBox(ctx, 'TA', TAAnnotationCoords.x, TAAnnotationCoords.y, color);
    ctx.restore();
  };

  drawMTPLine = (
    element: HTMLElement,
    ctx: CanvasRenderingContext2D,
    { MTP1, MTP2 }: Pick<TPLOAnnotation['handles'], 'MTP1' | 'MTP2'>,
    isLeftSideView: boolean,
    { color, lineWidth }: { color: string; lineWidth: number }
  ): void => {
    if (!MTP1 || !MTP2) return;
    drawLine(ctx, element, MTP1, MTP2, { color, lineWidth });

    const MTP1Canvas = csc.pixelToCanvas(element, MTP1 as csc.PixelCoordinate);
    const MTP2Canvas = csc.pixelToCanvas(element, MTP2 as csc.PixelCoordinate);
    const leftToRightMTPAxis: [Point, Point] =
      MTP2Canvas.x >= MTP1Canvas.x ? [MTP1, MTP2] : [MTP2, MTP1];

    const refPoint = isLeftSideView ? leftToRightMTPAxis[0] : leftToRightMTPAxis[1];

    const MTPAnnotationCoords = csc.pixelToCanvas(
      element,
      offsetPixelPointInCanvas(
        element,
        projectPerpendicularToAxis(element, refPoint, ...leftToRightMTPAxis, -20),
        { x: -CORNERSTONE_TEXT_PADDING, y: -CORNERSTONE_TEXT_PADDING }
      ) as csc.PixelCoordinate
    );

    ctx.save();
    if (!isLeftSideView) {
      ctx.textAlign = 'right';
    }
    drawTextBox(ctx, 'MTP', MTPAnnotationCoords.x, MTPAnnotationCoords.y, color);
    ctx.restore();
  };
}
